import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchMarineConditions, type MarineConditions } from '@/lib/marine';

/**
 * Condições marítimas (clima + mar + solunar). Busca na ENTRADA do app e
 * cacheia em localStorage. Só refaz a chamada se: (a) passou de MAX_AGE_MS,
 * (b) a localização de referência mudou bastante, ou (c) ainda não buscou
 * nesta sessão de navegador. Isso economiza chamadas às APIs externas.
 */

const MAX_AGE_MS = 3 * 60 * 60 * 1000; // 3 horas
const MOVE_THRESHOLD_DEG = 0.25; // ~25 km

interface ConditionsState {
  data: MarineConditions | null;
  fetchedAt: number | null;
  lat: number | null;
  lon: number | null;
  loading: boolean;
  error: string | null;
  /** Busca se necessário (stale/local mudou/sessão nova). force ignora cache. */
  refresh: (lat: number, lon: number, place: string | null, force?: boolean) => Promise<void>;
}

// Flag de "já buscou nesta sessão de navegador" (1 fetch por login/abertura).
let fetchedThisSession = false;

export const useConditionsStore = create<ConditionsState>()(
  persist(
    (set, get) => ({
      data: null,
      fetchedAt: null,
      lat: null,
      lon: null,
      loading: false,
      error: null,
      refresh: async (lat, lon, place, force = false) => {
        const s = get();
        const aged = !s.fetchedAt || Date.now() - s.fetchedAt > MAX_AGE_MS;
        const moved =
          s.lat == null ||
          s.lon == null ||
          Math.abs(s.lat - lat) > MOVE_THRESHOLD_DEG ||
          Math.abs(s.lon - lon) > MOVE_THRESHOLD_DEG;
        const sessionStale = !fetchedThisSession;

        if (!force && !aged && !moved && !sessionStale) return;
        if (s.loading) return;

        set({ loading: true, error: null });
        try {
          const data = await fetchMarineConditions(lat, lon, place);
          fetchedThisSession = true;
          set({ data, fetchedAt: Date.now(), lat, lon, loading: false, error: null });
        } catch (e) {
          set({ loading: false, error: e instanceof Error ? e.message : 'Falha ao buscar condições' });
        }
      },
    }),
    { name: 'nautmax-conditions' },
  ),
);
